import { mapEntityTypeFromDTO } from '@/modules/common/mappers/entity-type.mapper';
import { mapIntegrationProviderFromDTO } from '@/modules/common/mappers/index';
import { IntegrationDataStatusResponse } from '@/modules/common/types/dto/integration-data-status.dto';
import {
  IntegrationStatusDTO,
  OrganizationIntegrationStatusDTO,
} from '@/modules/common/types/dto/integration-status.dto';
import { IntegrationSyncingStatusDto } from '@/modules/common/types/dto/integration-syncing-status.dto';

import {
  ConflictDataResponse,
  EntityIntegrationStatusDescriptorResponse,
  IntegrationErrorResponse,
  IntegrationStatusDescriptorResponse,
  OrganizationIntegrationStatusApiResponse,
} from '../types/dto/response/integration-status-response';
import {
  ConflictData,
  EntityIntegrationStatusDescriptor,
  IntegrationDataStatus,
  IntegrationError,
  IntegrationStatus,
  IntegrationStatusDescriptor,
  IntegrationSyncingStatus,
  OrganizationIntegrationStatus,
  OrganizationIntegrationStatusData,
} from '../types/integration-status';

// ===== Integration Status Mappers =====

/**
 * Maps IntegrationStatusDTO from API to IntegrationStatus domain model
 */
export const mapIntegrationStatusFromDTO = (dto: IntegrationStatusDTO): IntegrationStatus => {
  switch (dto) {
    case IntegrationStatusDTO.CONNECTED:
      return IntegrationStatus.Connected;
    case IntegrationStatusDTO.ERROR:
      return IntegrationStatus.Error;
    case IntegrationStatusDTO.SYNCING:
      return IntegrationStatus.Syncing;
    case IntegrationStatusDTO.UNKNOWN:
    default:
      return IntegrationStatus.Unknown;
  }
};

/**
 * Maps IntegrationStatus domain model to IntegrationStatusDTO for API
 */
export const mapIntegrationStatusToDTO = (status: IntegrationStatus): IntegrationStatusDTO => {
  switch (status) {
    case IntegrationStatus.Connected:
      return IntegrationStatusDTO.CONNECTED;
    case IntegrationStatus.Error:
      return IntegrationStatusDTO.ERROR;
    case IntegrationStatus.Syncing:
      return IntegrationStatusDTO.SYNCING;
    case IntegrationStatus.Unknown:
    default:
      return IntegrationStatusDTO.UNKNOWN;
  }
};

// ===== Organization Integration Status Mappers =====

/**
 * Maps OrganizationIntegrationStatusDTO from API to OrganizationIntegrationStatus domain model
 */
export const mapOrganizationIntegrationStatusFromDTO = (
  dto: OrganizationIntegrationStatusDTO
): OrganizationIntegrationStatus => {
  switch (dto) {
    case OrganizationIntegrationStatusDTO.IDLE:
      return OrganizationIntegrationStatus.Idle;
    case OrganizationIntegrationStatusDTO.SYNCING:
      return OrganizationIntegrationStatus.Syncing;
    case OrganizationIntegrationStatusDTO.SYNC_FAILED:
      return OrganizationIntegrationStatus.SyncFailed;
    case OrganizationIntegrationStatusDTO.HAS_CONFLICTS:
      return OrganizationIntegrationStatus.HasConflicts;
    case OrganizationIntegrationStatusDTO.UNKNOWN:
    default:
      return OrganizationIntegrationStatus.Unknown;
  }
};

/**
 * Maps OrganizationIntegrationStatus domain model to OrganizationIntegrationStatusDTO for API
 */
export const mapOrganizationIntegrationStatusToDTO = (
  status: OrganizationIntegrationStatus
): OrganizationIntegrationStatusDTO => {
  switch (status) {
    case OrganizationIntegrationStatus.Idle:
      return OrganizationIntegrationStatusDTO.IDLE;
    case OrganizationIntegrationStatus.Syncing:
      return OrganizationIntegrationStatusDTO.SYNCING;
    case OrganizationIntegrationStatus.SyncFailed:
      return OrganizationIntegrationStatusDTO.SYNC_FAILED;
    case OrganizationIntegrationStatus.HasConflicts:
      return OrganizationIntegrationStatusDTO.HAS_CONFLICTS;
    case OrganizationIntegrationStatus.Unknown:
    default:
      return OrganizationIntegrationStatusDTO.UNKNOWN;
  }
};

// ===== Entity Integration Status Mappers =====

/**
 * Maps EntityIntegrationStatusDTO from API to EntityIntegrationStatus domain model
 */
export const mapIntegrationDataStatusFromDTO = (
  dto: IntegrationDataStatusResponse
): IntegrationDataStatus => {
  switch (dto) {
    case IntegrationDataStatusResponse.RESOLVED_CONFLICT_ERROR:
      return IntegrationDataStatus.ResolvedConflictError;
    case IntegrationDataStatusResponse.IDLE:
      return IntegrationDataStatus.Idle;
    case IntegrationDataStatusResponse.ERROR:
      return IntegrationDataStatus.Error;
    default:
      return IntegrationDataStatus.Idle;
  }
};

/**
 * Maps EntityIntegrationStatusDTO from API to EntityIntegrationStatus domain model
 */
export const mapIntegrationSyncingStatusFromDTO = (
  dto: IntegrationSyncingStatusDto
): IntegrationSyncingStatus => {
  switch (dto) {
    case IntegrationSyncingStatusDto.SYNCING:
      return IntegrationSyncingStatus.Syncing;
    case IntegrationSyncingStatusDto.IDLE:
      return IntegrationSyncingStatus.Idle;
    default:
      return IntegrationSyncingStatus.Idle;
  }
};

/**
 * Maps EntityIntegrationStatus domain model to EntityIntegrationStatusDTO for API
 */
export const mapEntityIntegrationStatusToDTO = (
  status: IntegrationSyncingStatus
): IntegrationSyncingStatusDto => {
  switch (status) {
    case IntegrationSyncingStatus.Syncing:
      return IntegrationSyncingStatusDto.SYNCING;
    case IntegrationSyncingStatus.Idle:
      return IntegrationSyncingStatusDto.IDLE;
    default:
      return IntegrationSyncingStatusDto.IDLE;
  }
};

// ===== Complex Object Mappers =====

/**
 * Maps IntegrationErrorResponse from API to IntegrationError domain model
 */
export const mapIntegrationErrorFromDTO = (dto: IntegrationErrorResponse): IntegrationError => {
  return {
    occurredAt: new Date(dto.occurredAt),
    code: dto.code,
    args: dto.args,
  };
};

/**
 * Maps EntityIntegrationStatusDescriptorResponse from API to domain model
 */
export const mapEntityIntegrationStatusDescriptorFromDTO = (
  dto: EntityIntegrationStatusDescriptorResponse
): EntityIntegrationStatusDescriptor => {
  return {
    syncingStatus: mapIntegrationSyncingStatusFromDTO(dto.syncingStatus),
    dataStatus: mapIntegrationDataStatusFromDTO(dto.dataStatus),
    entityType: mapEntityTypeFromDTO(dto.entityType),
    lastSyncErrors: dto.lastSyncErrors?.map(mapIntegrationErrorFromDTO),
    lastSyncedAt: dto.lastSyncedAt ? new Date(dto.lastSyncedAt) : undefined,
  };
};

/**
 * Maps IntegrationStatusDescriptorResponse from API to domain model
 */
export const mapIntegrationStatusDescriptorFromDTO = (
  dto: IntegrationStatusDescriptorResponse
): IntegrationStatusDescriptor => {
  return {
    integration: mapIntegrationProviderFromDTO(dto.integration),
    status: mapIntegrationStatusFromDTO(dto.status),
    entities: dto.entities.map(mapEntityIntegrationStatusDescriptorFromDTO),
    errors: dto.errors?.map(mapIntegrationErrorFromDTO),
    lastSyncedAt: dto.lastSyncedAt ? new Date(dto.lastSyncedAt) : undefined,
  };
};

export const mapConflictDataFromDTO = (dto: ConflictDataResponse): ConflictData => {
  return {
    entityType: mapEntityTypeFromDTO(dto.entityType),
    isResolved: dto.isResolved,
  };
};

/**
 * Maps OrganizationIntegrationStatusApiResponse from API to domain model
 */
export const mapOrganizationIntegrationStatusApiResponseFromDTO = (
  dto: OrganizationIntegrationStatusApiResponse
): OrganizationIntegrationStatusData => {
  return {
    status: mapOrganizationIntegrationStatusFromDTO(dto.status),
    integrations: dto.integrations.map(mapIntegrationStatusDescriptorFromDTO),
    conflicts: dto.conflicts.map(mapConflictDataFromDTO),
  };
};
